import { Model } from 'app/model/backendmodel';
import { Injectable } from '@angular/core';
import { IntegrationRuntimeOperationsService } from 'app/apis/integration-runtime-operations-api.service';
import { LoggerService } from 'app/logger/logger.service';
import { generateUniqueId } from 'app/shared/utils/naming.util';
import { MessagingService, MessagingServiceChannel } from 'app/services/messaging/messaging-service';
import {
    IExecuteSSISPackageActivityMetadataRefreshStartMessage,
    IExecuteSSISPackageActivityMetadataRefreshEndMessage,
    ExecuteSSISPackageActivityMetadataRefreshStatus
} from 'app/ui-model/integrationRuntime/ssis-activity';
import { MessageKind, Message } from 'app/services/messaging/message';
import { SsisObjectMetadataEvent, ssisObjectMetadataEventResult, ssisObjectMetadataDetailEvent } from 'app/logger/logger.ssisobjectmetadata-event';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';


@Injectable({
    providedIn: 'root'
})
export class ExecuteSSISPackageActivityMetadataOperationService {

    private static readonly componentName = 'ExecuteSSISPackageActivityMetadataOperationService';

    private static readonly metadataCache: IMetadataCache = {};
    private static readonly operationQueue: IMetadataOperation[] = [];
    private static readonly fetchOperationResults: IOperationResults = {};
    private static readonly fetchingPathSet: Set<string> = new Set<string>();
    private static readonly refreshingIrSet: Set<string> = new Set<string>();
    private static readonly OPERATION_FETCH = 'fetch';
    private static readonly OPERATION_REFRESH = 'refresh';

    constructor(private readonly irOperationsService: IntegrationRuntimeOperationsService, private readonly logger: LoggerService, private readonly messagingService: MessagingService) {
        this.startConsumeOperationAsync();
    }

    public static findSsisFolderFromCache(irName: string, folderName: string): Model.SsisFolder {
        const ssisFolders = ExecuteSSISPackageActivityMetadataOperationService.findMetadataFromCache(irName, `ssisFolders`) as Model.SsisFolder[];
        if (ssisFolders) {
            return ssisFolders.find((ssisFolder) => ssisFolder.name === folderName);
        }
        return null;
    }

    public static findSsisProjectWithoutParameterFromCache(irName: string, folderName: string, projectName: string): Model.SsisProject {
        const ssisFolder = ExecuteSSISPackageActivityMetadataOperationService.findSsisFolderFromCache(irName, folderName);
        if (ssisFolder) {
            const ssisProjects = ExecuteSSISPackageActivityMetadataOperationService.findMetadataFromCache(irName, `ssisFolders/${ssisFolder.id}/ssisProjects`) as Model.SsisProject[];
            if (ssisProjects) {
                return ssisProjects.find((ssisProject) => ssisProject.name === projectName);
            }
        }
        return null;
    }

    public static findSsisProjectWithParameterFromCache(irName: string, folderName: string, projectName: string): Model.SsisProject {
        const ssisProject = ExecuteSSISPackageActivityMetadataOperationService.findSsisProjectWithoutParameterFromCache(irName, folderName, projectName);
        if (ssisProject) {
            const ssisProjects = this.findMetadataFromCache(irName, `ssisFolders/${ssisProject.folderId}/ssisProjects/${ssisProject.id}`);
            return ssisProjects ? ssisProjects[0] : null;
        }
        return null;
    }

    public static findSsisPackageFromCache(irName: string, folderName: string, projectName: string, packageName: string): Model.SsisPackage {
        const ssisProject = ExecuteSSISPackageActivityMetadataOperationService.findSsisProjectWithoutParameterFromCache(irName, folderName, projectName);
        if (ssisProject) {
            const ssisPackages =
                ExecuteSSISPackageActivityMetadataOperationService.findMetadataFromCache(
                    irName, `ssisFolders/${ssisProject.folderId}/ssisProjects/${ssisProject.id}/ssisPackages`) as Model.SsisPackage[];
            if (ssisPackages) {
                return ssisPackages.find((ssisPackage) => ssisPackage.name === packageName);
            }
        }
        return null;
    }

    public static findSsisEnvironmentListFromCache(irName: string, folderName: string): Model.SsisEnvironment[] {
        const ssisFolder = ExecuteSSISPackageActivityMetadataOperationService.findSsisFolderFromCache(irName, folderName);
        if (ssisFolder) {
            return ExecuteSSISPackageActivityMetadataOperationService.findMetadataFromCache(
                irName, `ssisFolders/${ssisFolder.id}/ssisEnvironments`) as Model.SsisEnvironment[];
        }
        return null;
    }

    public static findSsisEnvironmentFromCache(irName: string, folderName: string, environmentPath: string): Model.SsisEnvironment {
        if (!environmentPath) {
            return null;
        }
        const paths = environmentPath.split('\/');
        if (paths.length !== 2) {
            return null;
        }
        const envFolderName = paths[0] === '.' ? folderName : paths[0];
        const envName = paths[1];
        const folder = ExecuteSSISPackageActivityMetadataOperationService.findSsisFolderFromCache(irName, envFolderName);
        if (!folder) {
             return null;
        }
        const environments = ExecuteSSISPackageActivityMetadataOperationService.findSsisEnvironmentListFromCache(irName, envFolderName);
        if (!environments) {
            return null;
        }
        return environments.find((env) => env.name === envName);
    }

    public static isIrUnderRefreshing(irName: string): boolean {
        return ExecuteSSISPackageActivityMetadataOperationService.refreshingIrSet.has(irName);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static findMetadataFromCache(irName: string, path: string): any {
        const irCache = ExecuteSSISPackageActivityMetadataOperationService.metadataCache[irName];
        if (irCache) {
            return irCache[path];
        }
        return null;
    }

    public async isIrStartedAsync(irName: string): Promise<boolean> {
        const irStatus = await this.irOperationsService.getStatusAsync(irName);
        return stringsAreEqualCaseInsensitive(irStatus.properties.state, 'Started');
    }

    public async refreshMetadataAsync(irName: string): Promise<void> {
        const irStarted = await (this.isIrStartedAsync(irName));
        if (!irStarted) {
            throw Error(stringResources.SsisActivityCannotRefreshMetadataForIrIsNotStarted);
        }
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_REFRESH,
            irName
        };
        this.enqueueOperation(operation);
    }

    public async fetchSsisFolderListAsync(irName: string): Promise<Model.SsisFolder[]> {
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH,
            irName,
            path: 'ssisFolders'
        };
        return this.fetchMetadataAsync(operation);
    }

    public async fetchSsisProjectListAsync(irName: string, folderId: number): Promise<Model.SsisProject[]> {
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH,
            irName,
            path: `ssisFolders/${folderId}/ssisProjects`
        };
        return this.fetchMetadataAsync(operation);
    }

    public async fetchSsisProjectAsync(irName: string, folderId: number, projectId: number): Promise<Model.SsisProject> {
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH,
            irName,
            path: `ssisFolders/${folderId}/ssisProjects/${projectId}`
        };
        const ssisProjects = await this.fetchMetadataAsync(operation) as Model.SsisProject[];
        return ssisProjects[0];
    }

    public async fetchSsisPackageListAsync(irName: string, folderId: number, projectId: number): Promise<Model.SsisPackage[]> {
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH,
            irName,
            path: `ssisFolders/${folderId}/ssisProjects/${projectId}/ssisPackages`
        };
        return this.fetchMetadataAsync(operation);
    }

    public async fetchSsisEnvironmentListAsync(irName: string, folderId: number): Promise<Model.SsisEnvironment[]> {
        const operation: IMetadataOperation = {
            id: generateUniqueId(),
            operation: ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH,
            irName,
            path: `ssisFolders/${folderId}/ssisEnvironments`
        };
        return this.fetchMetadataAsync(operation);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async fetchMetadataAsync(operation: IMetadataOperation): Promise<any> {
        this.enqueueOperation(operation);
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                const result = this.findFetchOperationResult(operation.id);
                this.removeFetchOperationResult(operation.id);
                if (result) {
                    if (result.error) {
                        reject(result.error);
                    } else {
                        resolve(result.result);
                    }
                    clearInterval(interval);
                }
            }, 100);
        });
    }

    private async startConsumeOperationAsync(): Promise<void> {
        setInterval(() => {
            const operation = this.dequeueOperation();
            if (!operation) {
                return;
            }
            if (operation.operation === ExecuteSSISPackageActivityMetadataOperationService.OPERATION_FETCH) {
                // init IR cache if necessary
                if (!ExecuteSSISPackageActivityMetadataOperationService.metadataCache[operation.irName]) {
                    ExecuteSSISPackageActivityMetadataOperationService.metadataCache[operation.irName] = {};
                }

                const cache = ExecuteSSISPackageActivityMetadataOperationService.findMetadataFromCache(operation.irName, operation.path);
                if (cache) {
                    this.addFetchOperationResult(operation.id, cache);
                } else if (this.isFetchingSamePath(operation)) {
                    // same request has been sent, wait for return
                    this.enqueueOperation(operation);
                } else {
                    this.addFetchingPath(operation);
                    this.executeFetchOperationAsync(operation);
                }
            } else if (operation.operation === ExecuteSSISPackageActivityMetadataOperationService.OPERATION_REFRESH) {
                if (ExecuteSSISPackageActivityMetadataOperationService.isIrUnderRefreshing(operation.irName)) {
                    // Do not do duplicated refresh
                    this.logger.logEvent(new SsisObjectMetadataEvent(operation.irName, ssisObjectMetadataDetailEvent.RefreshDrop));
                    return;
                }
                this.addRefreshingIr(operation.irName);
                this.executeRefreshOperationAsync(operation);
            }
        }, 100);
    }

    private async executeFetchOperationAsync(operation: IMetadataOperation): Promise<void> {
        const request: Model.GetSsisObjectMetadataRequest = {
            metadataPath: operation.path
        };
        try {
            const response = await this.irOperationsService.getSsisObjectMetadataAsync(operation.irName, request);
            this.updateCache(operation.irName, operation.path, response.value);
            this.addFetchOperationResult(operation.id, response.value);
        } catch (error) {
            this.logger.error(ExecuteSSISPackageActivityMetadataOperationService.componentName, error);
            this.addFetchOperationResult(operation.id, null, error);
        } finally {
            this.removeFetchingPath(operation);
        }
    }

    private async executeRefreshOperationAsync(operation: IMetadataOperation): Promise<void> {
        const irName = operation.irName;
        const startMessage: IExecuteSSISPackageActivityMetadataRefreshStartMessage = { irName };
        this.messagingService.broadcast(MessagingServiceChannel.ExecuteSSISPackageActivityMetadataRefreshStart,
            new Message<IExecuteSSISPackageActivityMetadataRefreshStartMessage>(MessageKind.Empty, startMessage));

        const refreshStartTime = new Date().getTime();
        this.logger.logEvent(new SsisObjectMetadataEvent(irName, ssisObjectMetadataDetailEvent.RefreshStart));
        await this.irOperationsService.refreshSsisObjectMetadataAndWaitForCompleteAsync(irName).then(() => {
            const timeCost = (new Date().getTime() - refreshStartTime) / 1000;
            this.logger.logEvent(new SsisObjectMetadataEvent(irName, ssisObjectMetadataDetailEvent.RefreshFinish, {
                result: ssisObjectMetadataEventResult.Succeeded,
                timeCostInSecond: timeCost.toString()
            }));

            this.clearCache(irName);
            this.removeRefreshingIr(irName);
            const endMessage: IExecuteSSISPackageActivityMetadataRefreshEndMessage = {
                irName,
                status: ExecuteSSISPackageActivityMetadataRefreshStatus.SUCCEEDED
            };
            this.messagingService.broadcast(MessagingServiceChannel.ExecuteSSISPackageActivityMetadataRefreshEnd,
                new Message<IExecuteSSISPackageActivityMetadataRefreshEndMessage>(MessageKind.Empty, endMessage));
        }).catch((error) => {
            const timeCost = (new Date().getTime() - refreshStartTime) / 1000;
            this.logger.logEvent(new SsisObjectMetadataEvent(irName, ssisObjectMetadataDetailEvent.RefreshFinish, {
                result: ssisObjectMetadataEventResult.Failed,
                timeCostInSecond: timeCost.toString(),
                error: error ? JSON.stringify(error) : null
            }));
            this.logger.error(ExecuteSSISPackageActivityMetadataOperationService.componentName, error);

            this.removeRefreshingIr(irName);
            const endMessage: IExecuteSSISPackageActivityMetadataRefreshEndMessage = {
                irName,
                status: ExecuteSSISPackageActivityMetadataRefreshStatus.FAILED,
                error
            };
            this.messagingService.broadcast(MessagingServiceChannel.ExecuteSSISPackageActivityMetadataRefreshEnd,
                new Message<IExecuteSSISPackageActivityMetadataRefreshEndMessage>(MessageKind.Empty, endMessage));
        });
    }

    private enqueueOperation(operation: IMetadataOperation): void {
        ExecuteSSISPackageActivityMetadataOperationService.operationQueue.push(operation);
    }

    private dequeueOperation(): IMetadataOperation {
        if (ExecuteSSISPackageActivityMetadataOperationService.operationQueue.length > 0) {
            return ExecuteSSISPackageActivityMetadataOperationService.operationQueue.pop();
        }
        return null;
    }

    private findFetchOperationResult(operationId: string): IOperationResult {
        return ExecuteSSISPackageActivityMetadataOperationService.fetchOperationResults[operationId];
    }

    private removeFetchOperationResult(operationId: string): void {
        delete ExecuteSSISPackageActivityMetadataOperationService.fetchOperationResults[operationId];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private addFetchOperationResult(operationId: string, result: any, error?: any): void {
        ExecuteSSISPackageActivityMetadataOperationService.fetchOperationResults[operationId] = {
            result,
            error
        };
    }

    private addFetchingPath(operation: IMetadataOperation): void {
        const path = this.getFetchingPath(operation);
        ExecuteSSISPackageActivityMetadataOperationService.fetchingPathSet.add(path);
    }

    private removeFetchingPath(operation: IMetadataOperation): void {
        const path = this.getFetchingPath(operation);
        ExecuteSSISPackageActivityMetadataOperationService.fetchingPathSet.delete(path);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private updateCache(irName: string, path: string, data: any): void {
        const irCache = ExecuteSSISPackageActivityMetadataOperationService.metadataCache[irName];
        irCache[path] = data;
    }

    private clearCache(irName: string): void {
        ExecuteSSISPackageActivityMetadataOperationService.metadataCache[irName] = {};
    }

    private isFetchingSamePath(operation: IMetadataOperation): boolean {
        const fetchingPath = this.getFetchingPath(operation);
        return ExecuteSSISPackageActivityMetadataOperationService.fetchingPathSet.has(fetchingPath);
    }

    private addRefreshingIr(irName: string): void {
        ExecuteSSISPackageActivityMetadataOperationService.refreshingIrSet.add(irName);
    }

    private removeRefreshingIr(irName: string): void {
        ExecuteSSISPackageActivityMetadataOperationService.refreshingIrSet.delete(irName);
    }

    private getFetchingPath(operation: IMetadataOperation): string {
        return operation.irName + '/' + operation.path;
    }

}

interface IMetadataOperation {
    id: string;
    operation: string;
    irName: string;
    path?: string;
}

interface IOperationResults {
    [id: string]: IOperationResult;
}

interface IOperationResult {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any;
}

export interface IMetadataCache {
    [irName: string]: IIntegrationRuntimeCache;
}

interface IIntegrationRuntimeCache {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [path: string]: any;
}
